import { backendApi } from '_config';

type CatalogTokenResponse = { token?: string; expiresAt?: string };

/**
 * The catalogue at /api/v1 is gated (`CATALOG_AUTH_MODE=enforce`) and wants a
 * short-lived JWT that Laravel mints from the session. Held in memory only:
 * it is cheap to re-fetch and must not outlive the tab.
 */
let cached: { token: string; expiresAt: number } | null = null;

/** First paint fires several catalogue queries at once; they share one mint. */
let inFlight: Promise<string | null> | null = null;

/** Refresh this far ahead of expiry so a token cannot die mid-request. */
const REFRESH_MARGIN_MS = 60_000;

/** Only for an unparseable expiresAt; the server mints for 3300s. */
const FALLBACK_TTL_MS = 300_000;

const mint = async (): Promise<string | null> => {
    try {
        // Web authenticates to Laravel with a session cookie, not a personal
        // access token, so the exchange has to carry credentials.
        const response = await fetch(`${backendApi}pattern-library/token`, {
            credentials: 'include',
        });
        if (!response.ok) {
            return null;
        }
        const { token, expiresAt } =
            (await response.json()) as CatalogTokenResponse;
        if (!token) {
            return null;
        }
        const parsed = Date.parse(expiresAt ?? '');
        cached = {
            token,
            expiresAt: Number.isFinite(parsed)
                ? parsed
                : Date.now() + FALLBACK_TTL_MS,
        };
        return token;
    } catch {
        // A failed mint means no header, not a crash: the 401 surfaces it.
        return null;
    }
};

export const getCatalogToken = async (): Promise<string | null> => {
    if (cached && cached.expiresAt - REFRESH_MARGIN_MS > Date.now()) {
        return cached.token;
    }
    inFlight ??= mint().finally(() => {
        inFlight = null;
    });
    return inFlight;
};

/**
 * Drop the held token, so the next call mints against the current session.
 * Called on a 401 and on logout — a token outlives the session that made it.
 */
export const clearCatalogToken = (): void => {
    cached = null;
};
